import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { sdk } from '@/lib/sdk'
import { useAuth } from '@/lib/AuthContext'
import { useMoney } from '@/lib/hooks/useMoney'
import {
  CustomerData,
  CustomerField,
  emptyCustomerData,
  accountDocument,
  nationalBrazilianPhone,
  toCustomerPayload,
  validateCustomerData
} from '@/lib/checkout/customer'
import { apiErrorMessage } from '@/lib/checkout/outcome'
import { CustomerDataFields } from '@/components/features/checkout/CustomerDataFields'
import { downsellCart, trackDownsellAccepted } from '@/lib/analytics/downsell'
import { trackAddPaymentInfo } from '@/lib/analytics/ecommerce'
import { getGaIds } from '@/lib/hooks/useGoogleAnalytics'
import { CopyField } from '@/components/features/checkout/PurchaseOutcomeView'
import type { PaymentMethod as SavedCard } from '@/types/payment-methods'
import type { DownsellAcceptResult, DownsellImpression, DownsellQuote } from '@/types/downsell'
import { OfferSummary } from './OfferSummary'

interface LastChanceOffersProps {
  /** Only the offers born from this plan subscription; all of the member's when omitted. */
  subscriptionId?: string
  /**
   * Right after paying, the offers are created by the payment webhook, which
   * can land a few seconds after the screen: keep asking every 5 s, a few times.
   */
  waitForOffers?: boolean
  /** Called after an offer is accepted, so the page can refresh what it shows. */
  onAccepted?: () => void
  /** How many offers are on screen, whenever that changes (the confirmation page holds its redirect for them). */
  onOffersChange?: (count: number) => void
  className?: string
}

const POLL_MS = 5000
const POLL_TRIES = 6

/**
 * "Last chance" (F-39): one card per add-on with an offer, after the plan is
 * paid and on /billing while it lasts. Accepting charges the saved card in one
 * click, or generates a PIX or boleto; "No, thanks" declines that offer. The
 * offer is marked seen when it is on screen, never on a GET.
 */
export function LastChanceOffers({ subscriptionId, waitForOffers = false, onAccepted, onOffersChange, className }: LastChanceOffersProps) {
  const { t } = useTranslation('billing')
  const [offers, setOffers] = useState<DownsellImpression[]>([])
  const [savedCard, setSavedCard] = useState<SavedCard | null>(null)
  const tries = useRef(0)

  const load = useCallback(async () => {
    try {
      const items = (await sdk.downsell.listImpressions(subscriptionId)) as DownsellImpression[]
      setOffers(items)
      return items.length
    } catch {
      return 0
    }
  }, [subscriptionId])

  useEffect(() => {
    let timer: number | undefined
    let alive = true
    const tick = async () => {
      const count = await load()
      tries.current += 1
      if (alive && waitForOffers && count === 0 && tries.current < POLL_TRIES) timer = window.setTimeout(tick, POLL_MS)
    }
    void tick()
    sdk.paymentMethods
      .list()
      .then((cards: SavedCard[]) => alive && setSavedCard(cards.find((c) => c.isDefault) || cards[0] || null))
      .catch(() => {})
    return () => {
      alive = false
      if (timer) window.clearTimeout(timer)
    }
  }, [load, waitForOffers])

  useEffect(() => {
    onOffersChange?.(offers.length)
  }, [offers.length, onOffersChange])

  if (offers.length === 0) return null

  return (
    <section className={cn('space-y-3', className)}>
      <div>
        <h3 className="flex items-center gap-2 font-semibold text-gray-900">
          <Sparkles className="h-4 w-4 text-primary" />
          {t('lastChance.title')}
        </h3>
        <p className="text-xs text-gray-500">{t('lastChance.subtitle')}</p>
      </div>
      {offers.map((offer) => (
        <OfferCard
          key={offer.id}
          offer={offer}
          savedCard={savedCard}
          onDeclined={() => setOffers((prev) => prev.filter((o) => o.id !== offer.id))}
          onAccepted={onAccepted}
        />
      ))}
    </section>
  )
}

interface OfferCardProps {
  offer: DownsellImpression
  savedCard: SavedCard | null
  onDeclined: () => void
  onAccepted?: () => void
}

type Method = 'CARD' | 'PIX' | 'BOLETO'

function OfferCard({ offer, savedCard, onDeclined, onAccepted }: OfferCardProps) {
  const { t } = useTranslation(['billing', 'checkout'])
  const { user } = useAuth()
  const formatMoney = useMoney()
  const [method, setMethod] = useState<Method>(savedCard ? 'CARD' : 'PIX')
  const [showMethods, setShowMethods] = useState(!savedCard)
  const [couponOpen, setCouponOpen] = useState(false)
  const [coupon, setCoupon] = useState('')
  const [quote, setQuote] = useState<DownsellQuote | null>(null)
  const [quoting, setQuoting] = useState(false)
  const [customer, setCustomer] = useState<CustomerData>(emptyCustomerData)
  const [errors, setErrors] = useState<Partial<Record<CustomerField, string>>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<DownsellAcceptResult | null>(null)
  const seen = useRef(false)

  // The card on screen is the offer seen (§6.4): once, and never from a GET.
  useEffect(() => {
    if (seen.current || offer.shownAt) return
    seen.current = true
    sdk.downsell.markSeen(offer.id).catch(() => {})
  }, [offer.id, offer.shownAt])

  // The saved card arrives after the first render: it becomes the default then.
  useEffect(() => {
    if (savedCard) {
      setMethod('CARD')
      setShowMethods(false)
    }
  }, [savedCard])

  useEffect(() => {
    if (!user) return
    setCustomer((prev) => ({ ...prev, name: prev.name || user.name || '', email: prev.email || user.email || '' }))
    sdk.auth
      .me()
      .then((me) => {
        const phone = nationalBrazilianPhone(typeof me?.phone === 'string' ? me.phone : null)
        if (phone) setCustomer((prev) => ({ ...prev, phone: prev.phone || phone }))
        const document = accountDocument(me)
        if (document) setCustomer((prev) => ({ ...prev, document: prev.document || document }))
      })
      .catch(() => {})
  }, [user])

  const applyCoupon = async () => {
    if (!coupon.trim()) return
    setQuoting(true)
    setError(null)
    try {
      setQuote((await sdk.downsell.quote(offer.id, { couponCode: coupon.trim() })) as DownsellQuote)
    } catch (e) {
      setQuote(null)
      setError(apiErrorMessage(e).message || t('lastChance.genericError'))
    } finally {
      setQuoting(false)
    }
  }

  const accept = async () => {
    if (busy) return
    setError(null)
    const floor = quote?.floorApplied ?? offer.quote?.floorApplied ?? false
    const byCard = method === 'CARD' && savedCard
    if (!floor && !byCard) {
      const found = validateCustomerData(customer)
      setErrors(found)
      if (Object.values(found).some(Boolean)) return
    }
    setBusy(true)
    const cart = downsellCart(offer, quote)
    try {
      if (!floor) trackAddPaymentInfo(cart, byCard ? 'CREDIT_CARD' : method)
      const gaIds = floor ? {} : await getGaIds()
      const response = (await sdk.downsell.accept(offer.id, {
        ...gaIds,
        ...(floor
          ? {}
          : byCard
            ? { paymentMethod: 'CREDIT_CARD', paymentMethodId: savedCard.id }
            : { paymentMethod: method, customerData: toCustomerPayload(customer) }),
        ...(quote?.coupon?.code ? { couponCode: quote.coupon.code } : {})
      })) as DownsellAcceptResult
      trackDownsellAccepted(response, cart, byCard ? 'CREDIT_CARD' : method)
      setResult(response)
      onAccepted?.()
    } catch (e) {
      setError(apiErrorMessage(e).message || t('lastChance.genericError'))
    } finally {
      setBusy(false)
    }
  }

  const decline = async () => {
    setBusy(true)
    try {
      await sdk.downsell.decline(offer.id)
    } catch {
      /* Declining is a courtesy to the metrics; the card goes away either way. */
    } finally {
      setBusy(false)
      onDeclined()
    }
  }

  if (result) {
    const code = result.pixCode || result.boletoBarcode
    return (
      <div className="space-y-3 rounded-xl border-2 border-green-200 bg-green-50/60 p-4">
        <p className="flex items-start gap-2 text-sm text-green-800">
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
          {result.charged && !result.pixCode && !result.boletoUrl && !result.boletoBarcode
            ? result.status === 'COMPLETED'
              ? t('lastChance.addedCharged', { title: offer.title })
              : t('lastChance.processing')
            : result.charged
              ? t('lastChance.payToFinish', { title: offer.title })
              : t('lastChance.addedFree', { title: offer.title })}
        </p>
        {result.qrCode && (
          <img
            src={result.qrCode}
            alt={t('purchase.outcome.pixQrAlt', { ns: 'checkout' })}
            className="mx-auto h-40 w-40 rounded-lg border border-gray-200 bg-white p-2"
          />
        )}
        {result.boletoUrl && (
          <Button asChild variant="secondary" size="sm">
            <a href={result.boletoUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />
              {t('purchase.outcome.openBoleto', { ns: 'checkout' })}
            </a>
          </Button>
        )}
        {code && (
          <CopyField
            value={code}
            label={result.pixCode ? t('purchase.outcome.pixCopyLabel', { ns: 'checkout' }) : t('purchase.outcome.boletoBarcode', { ns: 'checkout' })}
          />
        )}
      </div>
    )
  }

  const floor = quote?.floorApplied ?? offer.quote?.floorApplied ?? false
  const amount = quote?.amountToCharge ?? offer.quote?.amountToCharge ?? '0'

  return (
    <div className="space-y-3 rounded-xl border-2 border-primary-300 bg-white p-4">
      <OfferSummary
        offer={offer}
        amountToCharge={quote?.amountToCharge}
        floorApplied={quote?.floorApplied}
        nextChargeDate={quote?.nextChargeDate}
      />

      {quote && Number(quote.couponDiscount) > 0 && (
        <p className="text-xs text-green-700">
          {t('lastChance.couponApplied', { amount: formatMoney(Number(quote.couponDiscount), offer.currency) })}
        </p>
      )}

      {!couponOpen ? (
        <button type="button" className="text-xs font-medium text-primary underline" onClick={() => setCouponOpen(true)}>
          {t('lastChance.haveCoupon')}
        </button>
      ) : (
        <div className="flex gap-2">
          <Input
            value={coupon}
            onChange={(e) => setCoupon(e.target.value.toUpperCase())}
            placeholder={t('lastChance.couponPlaceholder')}
            aria-label={t('lastChance.couponPlaceholder')}
            className="h-9 min-w-0"
          />
          <Button type="button" variant="outline" size="sm" onClick={() => void applyCoupon()} disabled={quoting || !coupon.trim()}>
            {quoting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('lastChance.applyCoupon')}
          </Button>
        </div>
      )}

      {!floor && (
        <div className="space-y-2">
          {savedCard && !showMethods ? (
            <p className="text-xs text-gray-600">
              {t('lastChance.chargeCard', {
                amount: formatMoney(Number(amount), offer.currency),
                brand: (savedCard.cardBrand || '').replace(/^./, (c) => c.toUpperCase()),
                last4: savedCard.cardLastDigits || ''
              })}{' '}
              <button type="button" className="font-medium text-primary underline" onClick={() => {
                setShowMethods(true)
                setMethod('PIX')
              }}>
                {t('lastChance.otherMethod')}
              </button>
            </p>
          ) : (
            <>
              <div role="radiogroup" aria-label={t('purchase.paymentMethod', { ns: 'checkout' })} className="flex flex-wrap gap-2">
                {([...(savedCard ? ['CARD'] : []), 'PIX', 'BOLETO'] as Method[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={method === value}
                    onClick={() => setMethod(value)}
                    className={cn(
                      'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                      method === value ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    )}
                  >
                    {value === 'CARD' ? t('lastChance.methods.card') : value === 'PIX' ? 'PIX' : t('lastChance.methods.boleto')}
                  </button>
                ))}
              </div>
              {method !== 'CARD' && (
                <CustomerDataFields
                  value={customer}
                  errors={errors}
                  onChange={(field, value) => {
                    setCustomer((prev) => ({ ...prev, [field]: value }))
                    setErrors((prev) => ({ ...prev, [field]: undefined }))
                  }}
                  disabled={busy}
                />
              )}
            </>
          )}
        </div>
      )}

      {error && (
        <div role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button variant="secondary" className="w-full sm:w-auto" onClick={() => void accept()} disabled={busy}>
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('lastChance.accept')}
        </Button>
        <Button variant="outline" className="w-full sm:w-auto" onClick={() => void decline()} disabled={busy}>
          {t('lastChance.decline')}
        </Button>
      </div>
    </div>
  )
}
